import React from 'react';

// Color map for the "color" prop pattern
const COLOR_MAP: Record<string, string> = {
  red:    'bg-red-100 text-red-700 border border-red-200',
  green:  'bg-green-100 text-green-700 border border-green-200',
  blue:   'bg-blue-100 text-[#02357A] border border-blue-200',
  orange: 'bg-orange-100 text-orange-700 border border-orange-200',
  yellow: 'bg-yellow-100 text-yellow-700 border border-yellow-200',
  gray:   'bg-gray-100 text-gray-600 border border-gray-200',
  purple: 'bg-purple-100 text-purple-700 border border-purple-200',
  pink:   'bg-pink-100 text-pink-700 border border-pink-200',
  indigo: 'bg-indigo-100 text-indigo-700 border border-indigo-200',
  teal:   'bg-teal-100 text-teal-700 border border-teal-200',
};

// Status map for the "status" prop pattern
const STATUS_MAP: Record<string, string> = {
  ACTIVE:       'bg-green-100 text-green-700',
  INACTIVE:     'bg-gray-100 text-gray-500',
  PENDING:      'bg-amber-100 text-amber-700',
  APPROVED:     'bg-green-100 text-green-700',
  REJECTED:     'bg-red-100 text-red-700',
  COMPLETED:    'bg-green-100 text-green-700',
  CANCELLED:    'bg-gray-100 text-gray-500',
  SCHEDULED:    'bg-blue-100 text-[#02357A]',
  IN_PROGRESS:  'bg-blue-100 text-[#02357A]',
  OPEN:         'bg-amber-100 text-amber-700',
  CLOSED:       'bg-gray-100 text-gray-500',
  PAID:         'bg-green-100 text-green-700',
  UNPAID:       'bg-red-100 text-red-700',
  PARTIAL:      'bg-amber-100 text-amber-700',
  ADMITTED:     'bg-blue-100 text-[#02357A]',
  DISCHARGED:   'bg-gray-100 text-gray-500',
  VERIFIED:     'bg-green-100 text-green-700',
  DRAFT:        'bg-gray-100 text-gray-500',
  POSTED:       'bg-green-100 text-green-700',
  SUBMITTED:    'bg-blue-100 text-[#02357A]',
  PROCESSING:   'bg-blue-100 text-[#02357A]',
  FAILED:       'bg-red-100 text-red-700',
  SUCCESS:      'bg-green-100 text-green-700',
  AVAILABLE:    'bg-green-100 text-green-700',
  OCCUPIED:     'bg-red-100 text-red-700',
  RESERVED:     'bg-amber-100 text-amber-700',
  COLLECTED:    'bg-blue-100 text-[#02357A]',
  RECEIVED:     'bg-purple-100 text-purple-700',
  RESULTED:     'bg-green-100 text-green-700',
  CRITICAL:     'bg-red-600 text-white',
  STAT:         'bg-red-100 text-red-700',
  URGENT:       'bg-amber-100 text-amber-700',
  ROUTINE:      'bg-blue-50 text-[#02357A]',
  LINKED:       'bg-green-100 text-green-700',
  UNLINKED:     'bg-red-100 text-red-600',
};

interface Props {
  // New pattern: text + color
  text?: string;
  color?: string;
  // Old pattern: label + className or status
  label?: string;
  className?: string;
  status?: string;
}

export default function Badge({ text, color, label, className, status }: Props) {
  // Resolve display text
  const displayText = text ?? label ?? '';

  // Resolve CSS classes
  let resolvedClass = 'bg-gray-100 text-gray-600';

  if (className) {
    // Old pattern: className passed directly — use it
    resolvedClass = className;
  } else if (color && COLOR_MAP[color]) {
    // New pattern: color name like "red", "green" etc.
    resolvedClass = COLOR_MAP[color];
  } else if (status && STATUS_MAP[status]) {
    // Status-based coloring
    resolvedClass = STATUS_MAP[status];
  } else if (text && COLOR_MAP[color ?? '']) {
    resolvedClass = COLOR_MAP[color ?? ''];
  }

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${resolvedClass}`}>
      {displayText}
    </span>
  );
}
